import { useEffect, useMemo, useState } from 'react';

import { AppHeader, MainNav, PullIndicator, Segments } from '../components/AppHeader.jsx';
import { useUI } from '../components/ui/feedback.jsx';
import { usePullToRefresh, useBackDismiss } from '../hooks.js';
import { examCheck, pendingCountFor } from '../lib/progress.js';
import { fmtDate, fullName, tecajniki } from '../lib/format.js';
import * as api from '../lib/api.js';
import { useSola } from '../sola.jsx';
import { t } from '../i18n/index.js';

import { StudentList } from './admin/StudentList.jsx';
import { StudentDetail } from './admin/StudentDetail.jsx';
import { FieldDay } from './admin/FieldDay.jsx';
import { ContentEditor } from './admin/ContentEditor.jsx';
import { ExamAdmin } from './admin/ExamAdmin.jsx';
import { Sola } from './admin/Sola.jsx';
import { Platforma } from './admin/Platforma.jsx';
import { Student } from './Student.jsx';
import { Icon } from '../components/ui/Icon.jsx';
import { povzetekVirov, letaTecajev } from '../lib/viri.js';

/*
  Zaslon inštruktorja.

  Največ pet mest v navigaciji (palec na dnu telefona):
    Tečajniki  pregled, seznam, detajl
    Teren      dan na terenu
    Vsebine    zaključni test | razlage vaj
    Šola       nastavitve | program | inštruktorji | licenca
    Platforma  samo Luka: šole, prijave, odobritve
*/

const VIEWS = (platforma) => [
  { id: 'students', icon: 'users', label: t('Tečajniki') },
  { id: 'field', icon: 'mountain', label: t('Teren') },
  { id: 'content', icon: 'book', label: t('Vsebine') },
  { id: 'sola', icon: 'school', label: t('Šola') },
  ...(platforma ? [{ id: 'platforma', icon: 'globe', label: t('Platforma') }] : []),
];

export function Admin({
  session,
  data,
  explanations,
  clanstva = [],
  onRefresh,
  onSignOut,
  onSwitchSola,
  onSolaChanged,
  onProgramChanged,
  setExplanations,
}) {
  const { toast, confirm } = useUI();
  const { sola, program, pogoji, platforma, vloga } = useSola();
  const [view, setViewState] = useState('students');
  const [contentSeg, setContentSeg] = useState('test');
  const [selected, setSelectedState] = useState(null);

  /* Preklop razdelka ali izbira tečajnika naj pelje na vrh — sicer
     pristaneš sredi nove vsebine. */
  const toTop = () => window.scrollTo({ top: 0, behavior: 'instant' });
  const setView = (v) => {
    setViewState(v);
    toTop();
  };
  const setSelected = (v) => {
    setSelectedState(v);
    toTop();
  };
  const { distance, busy } = usePullToRefresh(onRefresh);
  const [bulkBusy, setBulkBusy] = useState(false);

  /* Predogled: inštruktor gleda aplikacijo skozi oči tečajnika.
     Pisanje je zaklenjeno v sloju api.js — glej setPreviewLock. */
  const [previewUid, setPreviewUid] = useState(null);
  const [previewDocs, setPreviewDocs] = useState(null);

  /* Zahteve za izbris osebnih podatkov. Po GDPR jih je treba obravnavati
     v razumnem roku, zato morajo biti vidne, ne skrite v nastavitvah. */
  const [zahteve, setZahteve] = useState([]);
  const { users, progress, flights, attendance, exams, ucenje = {} } = data;
  const userIds = useMemo(() => new Set(users.map((u) => u.id)), [users]);
  useEffect(() => {
    api
      .loadZahteveIzbris(null)
      .then((z) => setZahteve(z.filter((x) => userIds.has(x.user_id))))
      .catch(() => setZahteve([]));
  }, [userIds]);
  const odprteZahteve = zahteve.filter((z) => !z.resil);

  /* Povzetek se izračuna enkrat, ne ob vsakem izrisu vrstice. */
  const summary = useMemo(() => {
    let pending = 0;
    let ready = 0;
    let unpaid = 0;
    let brezDatuma = 0;
    const perUser = {};

    users.forEach((u) => {
      const p = progress[u.id] ?? {};
      const f = flights[u.id] ?? [];
      const n = pendingCountFor(p, f, program);
      const exam = examCheck(u, p, f, null, exams[u.id], { program, pogoji });
      perUser[u.id] = { pending: n, readyExDocs: exam.readyExDocs };

      if (u.testni || u.archived) return;
      pending += n;
      if (exam.readyExDocs) ready += 1;
      if (!u.placano) unpaid += 1;
      if (!u.datum_tecaja) brezDatuma += 1;
    });

    return {
      perUser,
      pending,
      ready,
      unpaid,
      brezDatuma,
      active: users.filter((u) => !u.archived && !u.testni).length,
    };
  }, [users, progress, flights, exams, program, pogoji]);

  const confirmAll = async (uid) => {
    const targets = uid ? users.filter((u) => u.id === uid) : users.filter((u) => !u.testni);
    let nSub = 0;
    targets.forEach((u) => {
      nSub += summary.perUser[u.id]?.pending ?? 0;
    });
    const nFl = targets.reduce(
      (a, u) => a + (flights[u.id] ?? []).filter((f) => f.status === 'pending').length,
      0,
    );
    if (!nSub && !nFl) {
      toast.info(t('Nič ne čaka potrditve.'));
      return;
    }

    const who = uid
      ? `${targets[0].ime} ${targets[0].priimek}`
      : `${targets.length} ${tecajniki(targets.length)}`;

    const ok = await confirm({
      title: t('Potrdim vse?'),
      body: t('Za {who}.', { who }),
      detail: [t('Vaje in leti skupaj: {n}', { n: nSub })],
      confirmLabel: t('Potrdi vse'),
    });
    if (!ok) return;

    setBulkBusy(true);
    try {
      await api.adminConfirmAll(targets.map((u) => u.id));
      toast.ok(t('Potrjeno.'));
      await onRefresh();
    } catch (e) {
      toast.bad(e.message ?? t('Potrjevanje ni uspelo.'));
    } finally {
      setBulkBusy(false);
    }
  };

  const previewUser = previewUid ? users.find((u) => u.id === previewUid) : null;

  useEffect(() => {
    if (!previewUid) {
      api.setPreviewLock(false);
      setPreviewDocs(null);
      return undefined;
    }
    api.setPreviewLock(true);
    let cancelled = false;
    api.loadDocMap(previewUid).then((m) => {
      if (!cancelled) setPreviewDocs(m);
    });
    return () => {
      cancelled = true;
      api.setPreviewLock(false);
    };
  }, [previewUid]);

  const sel = selected ? users.find((u) => u.id === selected) : null;

  // Sistemski „nazaj" se vrne na seznam, ne iz aplikacije.
  useBackDismiss(!!sel, () => setSelected(null));

  const me = useMemo(() => {
    const clan = clanstva.find((c) => c.sola_id === sola?.id) ?? clanstva[0];
    const email = session?.user?.email ?? '';
    const ime = clan?.ime ?? email.split('@')[0];
    const parts = ime.split(' ');
    return { ime: parts[0] ?? '', priimek: parts.slice(1).join(' ') };
  }, [clanstva, sola, session]);

  // Imena šol za preklop, če je inštruktor v več šolah.
  const [mojeSole, setMojeSole] = useState([]);
  useEffect(() => {
    if (platforma || clanstva.length < 2) return;
    api.loadSoleIme(clanstva.map((c) => c.sola_id)).then(setMojeSole).catch(() => {});
  }, [clanstva, platforma]);

  if (previewUser) {
    return (
      <Student
        preview
        onExitPreview={() => setPreviewUid(null)}
        session={{ user: { id: previewUser.id, email: previewUser.email } }}
        profile={previewUser}
        data={{
          progress: progress[previewUser.id] ?? {},
          flights: flights[previewUser.id] ?? [],
          attendance: attendance[previewUser.id] ?? [],
          exams: exams[previewUser.id] ?? [],
          ucenje: ucenje[previewUser.id] ?? [],
          docs: previewDocs ?? {},
        }}
        explanations={explanations}
        onRefresh={async () => {}}
        onSignOut={() => setPreviewUid(null)}
      />
    );
  }

  const vecSol = platforma || clanstva.length > 1;
  const zamrznjena = sola && !['preizkus', 'aktivna'].includes(sola.status);
  const potece = sola?.velja_do ? Math.round((new Date(sola.velja_do) - new Date()) / 86400000) : null;

  return (
    <>
      <PullIndicator distance={distance} busy={busy} />

      {/* Glej opombo v Student.jsx: transform bi razbil lepljivi meni. */}
      <main className="page page--wide">
        <AppHeader
          profile={me}
          role={vloga === 'platforma' ? t('Platforma') : vloga === 'lastnik' ? t('Lastnik šole') : t('Inštruktor')}
          onSignOut={onSignOut}
          onBack={sel ? () => setSelected(null) : undefined}
          extra={
            vecSol && !platforma ? (
              <select
                className="input"
                style={{ minHeight: 34, width: 'auto', fontSize: 'var(--fs-sm)' }}
                value={sola?.id ?? ''}
                aria-label={t('Šola')}
                onChange={(e) => onSwitchSola(e.target.value)}
              >
                {clanstva.map((c) => (
                  <option key={c.sola_id} value={c.sola_id}>
                    {mojeSole.find((x) => x.id === c.sola_id)?.ime ?? c.sola_id.slice(0, 8)}
                  </option>
                ))}
              </select>
            ) : null
          }
        />

        {zamrznjena && (
          <div className="card card--pad row notice notice--bad" role="alert">
            <Icon name="alert" size={24} className="t-bad" />
            <span className="spacer t-md">
              <strong className="t-bad">{t('Dostop šole je')} {sola.status === 'zaprta' ? t('zaprt') : t('zamrznjen')}.</strong>{' '}
              {t('Tečajniki in inštruktorji lahko berejo, ne morejo pa ničesar vpisati. Uredi licenco pod Šola → Licenca.')}
            </span>
          </div>
        )}
        {!zamrznjena && potece != null && potece <= 30 && (
          <div className="card card--pad row notice" style={{ background: 'var(--wait-wash)', borderColor: 'var(--wait-border)' }} role="status">
            <Icon name="clock" size={22} className="t-wait" />
            <span className="spacer t-sm">
              {t('Licenca šole')}{' '}
              {potece < 0
                ? t('je potekla {d}', { d: fmtDate(sola.velja_do) })
                : t('poteče čez {n} dni ({d})', { n: potece, d: fmtDate(sola.velja_do) })}
              . {t('Podaljšanje: Šola → Licenca.')}
            </span>
          </div>
        )}

        {view === 'students' && (
          <div className={`admin-top${sel ? ' admin-top--detail' : ''}`}>
            <Overview
              summary={summary}
              bulkBusy={bulkBusy}
              onConfirmAll={() => confirmAll(null)}
              odprteZahteve={odprteZahteve.length}
            />
            {odprteZahteve.length > 0 && (
              <ZahteveZaIzbris
                zahteve={odprteZahteve}
                users={users}
                onResolved={async () =>
                  setZahteve((await api.loadZahteveIzbris(null)).filter((x) => userIds.has(x.user_id)))
                }
              />
            )}
            <Viri users={users} />
          </div>
        )}

        <MainNav
          items={VIEWS(platforma).map((v) =>
            v.id === 'students' ? { ...v, badge: summary.pending } : v,
          )}
          value={view}
          onChange={(v) => {
            setView(v);
            setSelected(null);
          }}
          label={t('Pogledi')}
        />

        {view === 'field' && (
          <FieldDay users={users} progress={progress} onSaved={onRefresh} />
        )}

        {view === 'content' && (
          <>
            <Segments
              label={t('Vsebine')}
              value={contentSeg}
              onChange={setContentSeg}
              items={[
                { id: 'test', icon: 'clipboard', label: t('Test') },
                { id: 'razlage', icon: 'book', label: t('Razlage') },
              ]}
            />
            {contentSeg === 'test' ? (
              <ExamAdmin users={users} exams={exams} onRefresh={onRefresh} />
            ) : (
              <ContentEditor
                explanations={explanations}
                onSaved={async () => setExplanations(await api.loadExplanations(sola.id))}
              />
            )}
          </>
        )}

        {view === 'sola' && (
          <Sola
            users={users}
            onSolaChanged={onSolaChanged}
            onProgramChanged={onProgramChanged}
          />
        )}

        {view === 'platforma' && platforma && (
          <Platforma onOpenSola={onSwitchSola} currentSolaId={sola?.id} />
        )}

        {view === 'students' && (
          /* Ali je seznam ob detajlu ali pod njim, odloči CSS glede na širino. */
          <div className={`admin-split${sel ? ' admin-split--detail' : ''}`}>
            <StudentList
              users={users}
              summary={summary}
              progress={progress}
              selected={selected}
              onSelect={setSelected}
            />

            {sel ? (
              <StudentDetail
                key={sel.id}
                user={sel}
                progress={progress[sel.id] ?? {}}
                flights={flights[sel.id] ?? []}
                attendance={attendance[sel.id] ?? []}
                exams={exams[sel.id] ?? []}
                ucenje={ucenje[sel.id] ?? []}
                pending={summary.perUser[sel.id]?.pending ?? 0}
                bulkBusy={bulkBusy}
                onConfirmAll={() => confirmAll(sel.id)}
                onBack={() => setSelected(null)}
                onArchived={() => setSelected(null)}
                onPreview={() => setPreviewUid(sel.id)}
                onChanged={onRefresh}
                onDeleted={async () => {
                  setSelected(null);
                  await onRefresh();
                }}
              />
            ) : (
              <div className="empty admin-hint">
                <span className="empty__icon">
                  <Icon name="users" size={34} />
                </span>
                <span className="empty__title">{t('Izberi tečajnika')}</span>
                <span className="empty__hint">
                  {t('Seznam je razvrščen po številki, najnovejši zgoraj.')}
                  {users.length === 0 && (
                    <>
                      {' '}
                      {t('Tečajniki se vpišejo sami: pošlji jim povezavo')}{' '}
                      <code>{window.location.origin}/?sola={sola?.slug}</code> {t('ali kodo šole')}{' '}
                      <strong>{sola?.koda}</strong>.
                    </>
                  )}
                </span>
              </div>
            )}
          </div>
        )}
      </main>
    </>
  );
}

function Overview({ summary, bulkBusy, onConfirmAll, odprteZahteve = 0 }) {
  return (
    <section className="tiles" aria-label={t('Pregled')}>
      <Tile label={t('Aktivnih tečajnikov')} value={summary.active} />
      <Tile
        label={t('Čaka potrditve')}
        value={summary.pending}
        tone={summary.pending > 0 ? 'wait' : undefined}
        action={
          summary.pending > 0 && (
            <button className="btn btn--sm btn--ok" onClick={onConfirmAll} disabled={bulkBusy}>
              {bulkBusy ? t('Potrjujem …') : t('Potrdi vse')}
            </button>
          )
        }
      />
      <Tile
        label={t('Pripravljenih na izpit')}
        value={summary.ready}
        tone={summary.ready > 0 ? 'ok' : undefined}
      />
      <Tile label={t('Neplačanih')} value={summary.unpaid} tone={summary.unpaid > 0 ? 'bad' : undefined} />
      {summary.brezDatuma > 0 && (
        <Tile label={t('Brez datuma tečaja')} value={summary.brezDatuma} tone="wait" />
      )}
      {odprteZahteve > 0 && (
        <Tile label={t('Zahtev za izbris')} value={odprteZahteve} tone="bad" />
      )}
    </section>
  );
}

function Tile({ label, value, tone, action }) {
  const color = tone ? `var(--${tone}-text)` : 'var(--text)';
  return (
    <div className="stat">
      <span className="stat__label">{label}</span>
      <span className="stat__value" style={{ color }}>
        {value}
      </span>
      {action}
    </div>
  );
}

/* ── Kje nas najdejo ─────────────────────────────────────────
   Edina številka, po kateri se vidi, ali se oglaševanje izplača. Zložena je
   privzeto — ne gledaš je vsak dan, seznam tečajnikov pa mora ostati na vrhu.

   Brez odgovora šteje posebej: če polovica ne odgovori, je pregled polovico
   vreden in to mora biti vidno, ne skrito v deležih.              */

function Viri({ users }) {
  const [odprto, setOdprto] = useState(false);
  const [leto, setLeto] = useState(null);

  const leta = useMemo(() => letaTecajev(users), [users]);
  const p = useMemo(() => povzetekVirov(users, { leto }), [users, leto]);

  return (
    <section className="card">
      <button
        className="card__head"
        style={{ width: '100%', textAlign: 'left' }}
        aria-expanded={odprto}
        onClick={() => setOdprto((x) => !x)}
      >
        <span className="card__title spacer">{t('Kje nas najdejo')}</span>
        <Icon name={odprto ? 'up' : 'down'} size={18} className="t-dimmer" />
      </button>

      {odprto && (
        <div className="card__body stack stack--tight">
          {leta.length > 1 && (
            <div className="row row--wrap" style={{ gap: 'var(--s-2)' }}>
              <button
                className={`btn btn--sm ${leto === null ? 'btn--secondary' : 'btn--ghost'}`}
                onClick={() => setLeto(null)}
              >
                {t('Vsi')}
              </button>
              {leta.map((l) => (
                <button
                  key={l}
                  className={`btn btn--sm ${leto === l ? 'btn--secondary' : 'btn--ghost'}`}
                  onClick={() => setLeto(l)}
                >
                  {l}
                </button>
              ))}
            </div>
          )}

          {p.vrstice.length === 0 ? (
            <p className="t-sm t-dim">
              {t('Nihče še ni odgovoril. Vprašanje je v obrazcu ob vpisu — podatki se naberejo z novimi tečajniki.')}
            </p>
          ) : (
            <ul className="stack stack--tight" style={{ listStyle: 'none' }}>
              {p.vrstice.map((v) => (
                <li key={v.key} className="stack" style={{ gap: 2 }}>
                  <div className="row t-sm">
                    <span className="spacer">{t(v.label)}</span>
                    <span className="num t-bold">{v.n}</span>
                    <span className="num t-dim">{v.delez} %</span>
                  </div>
                  <div
                    aria-hidden="true"
                    style={{ height: 6, borderRadius: 3, background: 'var(--surface-3)' }}
                  >
                    <div
                      style={{
                        width: `${v.delez}%`,
                        height: '100%',
                        borderRadius: 3,
                        background: 'var(--brand)',
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}

          <p className="t-sm t-dim">
            {t('Odgovorilo {a} od {b} {w}.', { a: p.odgovorilo, b: p.skupaj, w: tecajniki(p.skupaj) })}
            {p.brezOdgovora > 0 && t(' Deleži so računani med tistimi, ki so odgovorili.')}
          </p>
        </div>
      )}
    </section>
  );
}

function ZahteveZaIzbris({ zahteve, users, onResolved }) {
  const { toast, confirm } = useUI();

  const resi = async (z) => {
    const u = users.find((x) => x.id === z.user_id);
    const ok = await confirm({
      title: t('Označim zahtevo za rešeno?'),
      body: `${fullName(u) ?? t('Tečajnik')} — ${t('vložena')} ${fmtDate(z.vlozeno)}.`,
      detail: [
        t('Najprej dejansko izbriši, kar je mogoče izbrisati.'),
        t('Evidenc šolanja in zaključnega testa ne brišeš — te mora šola hraniti.'),
        t('Tečajniku sporoči, kaj je izbrisano in kaj je obdržano ter zakaj.'),
      ],
      confirmLabel: t('Označi za rešeno'),
    });
    if (!ok) return;
    try {
      await api.resiZahtevoZaIzbris(z.id, null);
      toast.ok(t('Zahteva je označena za rešeno.'));
      await onResolved();
    } catch (e) {
      toast.bad(e.message ?? t('Ni uspelo.'));
    }
  };

  return (
    <section
      className="card card--pad stack stack--tight"
      style={{ background: 'var(--bad-wash)', borderColor: 'var(--bad-border)' }}
    >
      <h2 className="t-lg t-bad">
        {zahteve.length === 1 ? t('Zahteva za izbris podatkov') : t('Zahteve za izbris podatkov ({n})', { n: zahteve.length })}
      </h2>
      <p className="t-sm t-dim">
        {t('Po GDPR jih obravnavaj v razumnem roku. Evidence šolanja in zaključnega testa ne brišeš — te mora šola hraniti. Tečajniku sporoči, kaj je izbrisano in kaj obdržano.')}
      </p>

      {zahteve.map((z) => {
        const u = users.find((x) => x.id === z.user_id);
        return (
          <div key={z.id} className="card card--pad row row--wrap">
            <span className="spacer">
              <strong className="t-md">{fullName(u) ?? t('Neznan tečajnik')}</strong>
              <span className="t-sm t-dim"> · {t('vložena')} {fmtDate(z.vlozeno)}</span>
              {z.sporocilo && <p className="t-sm t-dim">„{z.sporocilo}“</p>}
            </span>
            {u?.email && (
              <a className="btn btn--sm btn--ghost" href={`mailto:${u.email}`}>
                {t('Odgovori')}
              </a>
            )}
            <button className="btn btn--sm btn--secondary" onClick={() => resi(z)}>
              {t('Označi za rešeno')}
            </button>
          </div>
        );
      })}
    </section>
  );
}
